<template>
  <div class="row">
    <div class="flex xs12">
      <va-card title="推送/代投趋势">
        <div class="row align--center mb-2">
          <div class="flex md3 xs12">
            <va-select
              label="时间范围"
              v-model="filters.periodPreset"
              :options="periodPresetOptions"
              key-by="value"
              @input="onPeriodPresetChange"
            />
          </div>
          <div class="flex md3 xs12">
            <va-date-picker
              label="自定义日期区间"
              :config="{ mode: 'range' }"
              v-model="filters.dateRange"
              @input="onDateRangeChange"
            />
          </div>
          <div class="flex md3 xs12">
            <va-button color="info" :loading="loading" @click="fetchStats">查询</va-button>
          </div>
          <div class="flex md3 xs12">
            <va-button flat color="gray" @click="$router.push({ name: 'pushDeliverStats' })">
              返回列表
            </va-button>
          </div>
        </div>

        <p class="mb-2">用户 uid：<b>{{ uid }}</b></p>

        <div class="mb-3">
          <va-collapse with-background>
            <span slot="header">求职问卷{{ survey ? '' : '（无数据）' }}</span>
            <div slot="body" class="kv-body">
              <div v-if="survey">
                <div v-for="group in surveyGroups" :key="group.title" class="kv-group">
                  <div class="kv-group__title">{{ group.title }}</div>
                  <div class="row">
                    <div class="flex md4 sm6 xs12 kv-item" v-for="item in group.items" :key="item.label">
                      <span class="kv-item__label">{{ item.label }}</span>
                      <a
                        v-if="item.link"
                        class="kv-item__value kv-item__value--link"
                        :href="item.link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ item.value }}</a>
                      <span v-else class="kv-item__value">{{ item.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else class="mb-0">该用户没有提交问卷。</p>
            </div>
          </va-collapse>
        </div>

        <div class="mb-3">
          <va-collapse with-background>
            <span slot="header">自动代投 Profile{{ profile ? '' : '（未创建）' }}</span>
            <div slot="body" class="kv-body">
              <div v-if="profile">
                <div v-for="group in profileGroups" :key="group.title" class="kv-group">
                  <div class="kv-group__title">{{ group.title }}</div>
                  <div class="row">
                    <div class="flex md4 sm6 xs12 kv-item" v-for="item in group.items" :key="item.label">
                      <span class="kv-item__label">{{ item.label }}</span>
                      <a
                        v-if="item.link"
                        class="kv-item__value kv-item__value--link"
                        :href="item.link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >{{ item.value }}</a>
                      <span v-else class="kv-item__value">{{ item.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <p v-else class="mb-0">该用户还没有创建自动代投 Profile，不会产生代投任务。</p>
            </div>
          </va-collapse>
        </div>

        <div class="row align--center mb-3" v-if="summary">
          <div class="flex md2 xs6">
            <va-card stripe="success" :title="String(summary.recommendTotal)">推荐岗位总数</va-card>
          </div>
          <div class="flex md2 xs6">
            <va-card stripe="warning" :title="String(summary.recommendApify)">其中 Apify 兜底</va-card>
          </div>
          <div class="flex md2 xs6">
            <va-card stripe="info" :title="String(summary.deliverSuccess)">代投成功</va-card>
          </div>
          <div class="flex md2 xs6">
            <va-card stripe="danger" :title="String(summary.deliverFailed)">代投失败</va-card>
          </div>
          <div class="flex md2 xs6">
            <va-card stripe="success" :title="formatRate(summary.successRate)">代投成功率</va-card>
          </div>
        </div>

        <va-alert color="warning" class="mb-3">
          <b>Apify 兜底岗位</b>：ES 岗位不足 10 个时由 openjob-push 从 LinkedIn 抓取补充。
          这些岗位会出现在推荐邮件里，但<b>没有打分、也没有入库</b>，在岗位库中查不到，因此不会触发自动代投。
        </va-alert>

        <va-alert color="info" class="mb-3">
          统计口径：推荐岗位来自岗位推送记录，<b>不含实时推送邮件</b>（该链路不回写推送记录表）；
          代投只统计终态（成功 / 重试耗尽），草稿、投递中、重试中的记录不计入。
        </va-alert>

        <div v-if="hasData">
          <h6 class="mt-3 mb-2">数量趋势</h6>
          <div class="row align--center mb-1">
            <div class="flex" v-for="s in seriesOptions" :key="s.key">
              <va-checkbox v-model="visibleSeries[s.key]" :label="s.label"/>
            </div>
          </div>
          <daily-trend-chart
            v-if="hasVisibleSeries"
            :chart-data="countChartData"
            :options="countChartOptions"
            :styles="countChartStyles"
          />
          <va-alert color="gray" v-else>请至少勾选一条曲线。</va-alert>

          <h6 class="mt-4 mb-2">代投成功率趋势</h6>
          <p class="chart-note mb-1">
            折线断开的日期表示<b>当天没有代投终态记录</b>（没投递），与 <b>0%</b>（投了但全部失败）不是一回事。
          </p>
          <daily-trend-chart
            :chart-data="rateChartData"
            :options="rateChartOptions"
            :styles="rateChartStyles"
          />
        </div>
        <va-alert color="gray" v-else-if="!loading">所选时间范围内没有数据。</va-alert>
      </va-card>
    </div>
  </div>
</template>

<script>
import autoApplyStatsApi from '../../api/autoApplyStats'
import openJobApi from '../../api/openJob'
import DailyTrendChart from './DailyTrendChart'

/** 与 getUser.vue 的同名映射保持一致 */
const CAREER_STAGE_TEXT = {
  1: 'Early Career (0-2 years)',
  2: 'Mid-Career (3-5 years)',
  3: 'Senior Career (6 years and above)',
  4: 'Seasoned Professional (10 years and above)',
}

function formatCareerStage (careerStage) {
  if (careerStage === null || careerStage === undefined || careerStage === '') {
    return '-'
  }
  return String(careerStage).split(',')
    .map(Number)
    .sort((a, b) => a - b)
    .map(s => CAREER_STAGE_TEXT[s] || s)
    .join(', ')
}

function formatEmploymentType (type) {
  if (type === 1) return 'Internship'
  if (type === 2) return 'Full-Time Employment'
  return 'Both'
}

function formatNoticeFrequency (freq) {
  return freq === 2 ? 'daily（每日）' : 'weekly（每周）'
}

function formatVisaSponsorship (visa) {
  if (visa === 0) return '不需要'
  if (visa === 1) return '需要'
  return '都可以'
}

/** 半径是修饰地点的，单独成列会被三列栅格换行拆散，合并成一个字段 */
function formatPreferredLocation (location, radiusMiles) {
  if (location === null || location === undefined || location === '') {
    return '-'
  }
  if (radiusMiles === null || radiusMiles === undefined || radiusMiles === '') {
    return String(location)
  }
  return `${location}（半径 ${radiusMiles} 英里）`
}

/** 空值统一显示为 '-'，避免页面上出现 null/undefined */
function show (value) {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  return String(value)
}

const PERIOD_PRESETS = {
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  THIS_MONTH: 'this_month',
  LAST_3_MONTHS: 'last_3_months',
}

function startOfDay (date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function endOfDay (date) {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

function toUnix (date) {
  return Math.floor(date.getTime() / 1000)
}

function selectValue (value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
    return value.value
  }
  return value
}

function parseDateRangeValue (dateRange) {
  if (!dateRange) {
    return null
  }
  if (Array.isArray(dateRange) && dateRange.length === 2) {
    return dateRange
  }
  if (typeof dateRange === 'string') {
    const parts = dateRange.split(' to ')
    if (parts.length === 2) {
      return parts
    }
  }
  return null
}

function parseYmd (ymd) {
  const [year, month, day] = ymd.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export default {
  name: 'PushDeliverStatsDetail',
  components: { DailyTrendChart },
  data () {
    return {
      loading: false,
      days: [],
      summary: null,
      survey: null,
      profile: null,
      filters: {
        periodPreset: PERIOD_PRESETS.LAST_30_DAYS,
        dateRange: null,
      },
      periodPresetOptions: [
        { text: '近 7 天', value: PERIOD_PRESETS.LAST_7_DAYS },
        { text: '近 30 天', value: PERIOD_PRESETS.LAST_30_DAYS },
        { text: '本月', value: PERIOD_PRESETS.THIS_MONTH },
        { text: '近 3 个月', value: PERIOD_PRESETS.LAST_3_MONTHS },
      ],
      seriesOptions: [
        { key: 'recommendTotal', label: '推荐岗位总数' },
        { key: 'recommendApify', label: 'Apify 兜底' },
        { key: 'deliverSuccess', label: '代投成功' },
        { key: 'deliverFailed', label: '代投失败' },
      ],
      visibleSeries: {
        recommendTotal: true,
        recommendApify: true,
        deliverSuccess: true,
        deliverFailed: true,
      },
    }
  },
  computed: {
    uid () {
      return this.$route.params.uid
    },
    hasData () {
      return this.days.length > 0
    },
    labels () {
      return this.days.map(d => d.day)
    },
    /**
     * 图高度必须走 styles（作用于 vue-chartjs 外层 div），不能用 height prop——
     * 后者是直接写到 canvas 上的像素属性，配合 maintainAspectRatio: false 会把图压扁。
     * position: relative 是 chart.js responsive 模式对父容器的要求。
     */
    countChartStyles () {
      return { height: '340px', position: 'relative' }
    },
    rateChartStyles () {
      return { height: '260px', position: 'relative' }
    },
    surveyGroups () {
      const s = this.survey
      if (!s) {
        return []
      }
      return [
        {
          title: '基本信息',
          items: [
            { label: '姓名', value: show(s.name) },
            { label: '邮箱', value: show(s.email) },
            { label: '简历', value: show(s.resumeUrl), link: s.resumeUrl || null },
          ],
        },
        {
          // 顺序经过排布：三列栅格下「地区」和「偏好地点」要落在同一行，
          // 插在中间才不会被换行拆开
          title: '求职偏好',
          items: [
            { label: '职位方向', value: show(s.workAreas) },
            { label: '雇佣类型', value: formatEmploymentType(s.employmentType) },
            { label: '职业阶段', value: formatCareerStage(s.careerStage) },
            { label: '地区', value: show(s.region) },
            { label: '偏好地点', value: formatPreferredLocation(s.preferredLocation, s.preferredRadiusMiles) },
            { label: 'Remote 偏好', value: show(s.remotePreference) },
            { label: '签证需求', value: formatVisaSponsorship(s.visaSponsorship) },
            { label: '推送频率', value: formatNoticeFrequency(s.noticeFrequency) },
          ],
        },
        {
          title: '其他',
          items: [
            { label: '求职困扰', value: show(s.concerns) },
            { label: '希望帮助', value: show(s.assistanceAreas) },
            { label: '建议', value: show(s.suggestions) },
          ],
        },
      ]
    },
    profileGroups () {
      const p = this.profile
      if (!p) {
        return []
      }
      // 刻意不展示 applyEmailPassword：那是代投邮箱的明文密码，页面上没有展示它的理由
      return [
        {
          title: '联系方式',
          items: [
            { label: '姓', value: show(p.lastName) },
            { label: '名', value: show(p.firstName) },
            { label: '个人邮箱', value: show(p.personalEmail) },
            { label: '代投邮箱', value: show(p.applyEmail) },
            { label: '电话', value: show([p.phoneCountryCode, p.phone].filter(Boolean).join(' ')) },
          ],
        },
        {
          title: '地址',
          items: [
            { label: '国家', value: show(p.country) },
            { label: '州/省', value: show(p.state) },
            { label: '城市', value: show(p.city) },
            { label: '街道', value: show(p.street) },
            { label: '邮编', value: show(p.zipCode) },
          ],
        },
        {
          title: '链接',
          items: [
            { label: 'LinkedIn', value: show(p.linkedin), link: p.linkedin || null },
            { label: 'GitHub', value: show(p.githubUrl), link: p.githubUrl || null },
            { label: 'Portfolio', value: show(p.portfolioUrl), link: p.portfolioUrl || null },
          ],
        },
        {
          title: '求职条件',
          items: [
            { label: '期望薪资', value: show(p.expectedSalary) },
            { label: '到岗周期', value: show(p.noticePeriod) },
            { label: '工作年限', value: show(p.yearsOfExperience) },
            { label: '愿意搬迁', value: show(p.willingToRelocate) },
            { label: '匹配阈值', value: p.matchThreshold === null || p.matchThreshold === undefined ? '-' : p.matchThreshold + ' 分' },
          ],
        },
        {
          title: '工作授权',
          items: [
            { label: '工作授权', value: show(p.workAuthorization) },
            { label: '是否有工作许可', value: show(p.authorizedToWork) },
            { label: '是否需要担保', value: show(p.requireSponsorship) },
          ],
        },
        {
          title: 'EEO 信息',
          items: [
            { label: '性别', value: show(p.gender) },
            { label: '族裔', value: show(p.raceEthnicity) },
            { label: '西语裔', value: show(p.hispanicLatino) },
            { label: '性取向', value: show(p.sexualOrientation) },
            { label: 'LGBTQ', value: show(p.lgbtq) },
            { label: '残障状态', value: show(p.disabilityStatus) },
            { label: '退伍军人', value: show(p.veteranStatus) },
          ],
        },
      ]
    },
    hasVisibleSeries () {
      return this.seriesOptions.some(s => this.visibleSeries[s.key])
    },
    /** 当前勾选的曲线里，是否还有落在左轴/右轴上的，用来决定对应 y 轴是否显示 */
    showRecommendAxis () {
      return this.visibleSeries.recommendTotal || this.visibleSeries.recommendApify
    },
    showDeliverAxis () {
      return this.visibleSeries.deliverSuccess || this.visibleSeries.deliverFailed
    },
    countChartData () {
      return {
        labels: this.labels,
        // 按勾选过滤：datasets 数量变化会让 vue-chartjs 重建图表，
        // 这正是我们要的——被隐藏的曲线不该继续参与 y 轴取值范围的计算
        datasets: this.allCountDatasets.filter(d => this.visibleSeries[d.seriesKey]),
      }
    },
    allCountDatasets () {
      return [
        {
          seriesKey: 'recommendTotal',
          label: '推荐岗位总数',
          data: this.days.map(d => d.recommendTotal),
          borderColor: '#4ae387',
          backgroundColor: 'rgba(74, 227, 135, 0.1)',
          fill: false,
          lineTension: 0.2,
          yAxisID: 'y-recommend',
        },
        {
          seriesKey: 'recommendApify',
          label: 'Apify 兜底（未打分/未入库）',
          data: this.days.map(d => d.recommendApify),
          borderColor: '#ffc107',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          borderDash: [6, 4],
          fill: false,
          lineTension: 0.2,
          yAxisID: 'y-recommend',
        },
        {
          seriesKey: 'deliverSuccess',
          label: '代投成功',
          data: this.days.map(d => d.deliverSuccess),
          borderColor: '#2c82e0',
          backgroundColor: 'rgba(44, 130, 224, 0.1)',
          fill: false,
          lineTension: 0.2,
          yAxisID: 'y-deliver',
        },
        {
          seriesKey: 'deliverFailed',
          label: '代投失败',
          data: this.days.map(d => d.deliverFailed),
          borderColor: '#e34a4a',
          backgroundColor: 'rgba(227, 74, 74, 0.1)',
          fill: false,
          lineTension: 0.2,
          yAxisID: 'y-deliver',
        },
      ]
    },
    countChartOptions () {
      // 推荐岗位（上百）与代投（十几）量级差一个数量级，共用一个 y 轴时
      // 代投两条线会紧贴底部看不出起伏，因此分左右两个轴
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        tooltips: { mode: 'index', intersect: false },
        hover: { mode: 'index', intersect: false },
        scales: {
          yAxes: [
            {
              id: 'y-recommend',
              position: 'left',
              display: this.showRecommendAxis,
              ticks: { beginAtZero: true, precision: 0 },
              scaleLabel: { display: true, labelString: '推荐岗位数' },
            },
            {
              id: 'y-deliver',
              position: 'right',
              display: this.showDeliverAxis,
              ticks: { beginAtZero: true, precision: 0 },
              scaleLabel: { display: true, labelString: '代投数' },
              // 右轴只在左轴也显示时才省略网格线，否则单独看代投时会没有横向参考线
              gridLines: { drawOnChartArea: !this.showRecommendAxis },
            },
          ],
        },
      }
    },
    rateChartData () {
      return {
        labels: this.labels,
        datasets: [
          {
            label: '代投成功率 (%)',
            // successRate 为 null 表示当天没有终态记录，chart.js 会在此处断线，
            // 这比补 0 更准确——0% 意味着"投了但全失败"，与"当天没有投递"是两回事
            data: this.days.map(d => d.successRate),
            borderColor: '#8ba2b3',
            backgroundColor: '#8ba2b3',
            fill: false,
            lineTension: 0.2,
            // 没有终态记录的天是 null，这里刻意断线：0% 意味着"投了但全失败"，
            // 与"当天根本没有投递"是两回事。孤立的天只剩一个点，所以点画大些
            spanGaps: false,
            pointRadius: 5,
            pointHoverRadius: 7,
          },
        ],
      }
    },
    rateChartOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        tooltips: {
          callbacks: {
            label: item => `代投成功率 ${item.yLabel}%`,
          },
        },
        scales: {
          yAxes: [{
            ticks: { beginAtZero: true, max: 100, stepSize: 20 },
            scaleLabel: { display: true, labelString: '成功率 (%)' },
          }],
        },
      }
    },
  },
  created () {
    this.fetchStats()
    this.fetchUserDetail()
  },
  methods: {
    /**
     * 问卷和代投 Profile 只在进页面时拉一次——它们不随时间范围变化，
     * 所以不放进 fetchStats。两个接口互相独立，任一失败不影响另一个和趋势图。
     */
    fetchUserDetail () {
      openJobApi.getUser({ uid: this.uid })
        .then(response => {
          this.survey = response && response.jobSurvey ? response.jobSurvey : null
        })
        .catch(() => {
          this.survey = null
        })
      openJobApi.getJobUserAutoApply(this.uid)
        .then(response => {
          this.profile = response && response.profile ? response.profile : null
        })
        .catch(() => {
          this.profile = null
        })
    },
    onPeriodPresetChange () {
      this.filters.dateRange = null
      this.fetchStats()
    },
    onDateRangeChange () {
      if (parseDateRangeValue(this.filters.dateRange)) {
        this.fetchStats()
      }
    },
    buildQueryParams () {
      const params = { uid: this.uid }
      const dateRange = parseDateRangeValue(this.filters.dateRange)
      if (dateRange) {
        params.startTime = toUnix(startOfDay(parseYmd(dateRange[0])))
        params.endTime = toUnix(endOfDay(parseYmd(dateRange[1])))
        return params
      }
      const range = this.resolvePeriodPresetRange(selectValue(this.filters.periodPreset))
      if (range) {
        params.startTime = range.startTime
        params.endTime = range.endTime
      }
      return params
    },
    resolvePeriodPresetRange (preset) {
      const now = new Date()
      if (preset === PERIOD_PRESETS.LAST_7_DAYS) {
        const start = new Date(now)
        start.setDate(start.getDate() - 6)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      if (preset === PERIOD_PRESETS.LAST_30_DAYS) {
        const start = new Date(now)
        start.setDate(start.getDate() - 29)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      if (preset === PERIOD_PRESETS.THIS_MONTH) {
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      if (preset === PERIOD_PRESETS.LAST_3_MONTHS) {
        const start = new Date(now.getFullYear(), now.getMonth() - 2, 1)
        return { startTime: toUnix(startOfDay(start)), endTime: toUnix(endOfDay(now)) }
      }
      return null
    },
    fetchStats () {
      if (!this.uid) {
        return
      }
      this.loading = true
      autoApplyStatsApi.getUserDailyStats(this.buildQueryParams())
        .then(response => {
          this.days = response.days || []
          this.summary = response.summary || null
          this.loading = false
        })
        .catch(() => {
          this.days = []
          this.summary = null
          this.loading = false
        })
    },
    formatRate (rate) {
      if (rate === null || rate === undefined) {
        return '-'
      }
      return `${rate}%`
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-note {
  font-size: 0.85rem;
  color: #8396a5;
}

.kv-body {
  padding: 0.5rem 0.25rem 0.25rem;
}

/* 问卷和代投 Profile 两个面板共用这套结构，样式改一处即可 */
.kv-group {
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }

  &__title {
    font-weight: 600;
    color: #34495e;
    margin-bottom: 0.5rem;
    padding-bottom: 0.3rem;
    border-bottom: 1px solid #dde5ec;
  }
}

.kv-item {
  display: flex;
  padding: 0.15rem 0;
  font-size: 0.9rem;

  &__label {
    color: #8396a5;
    min-width: 7rem;
    flex-shrink: 0;
  }

  &__value {
    color: #34495e;
    word-break: break-all;

    &--link {
      color: #2c82e0;
      text-decoration: underline;

      &:hover {
        color: #1a5fa8;
      }
    }
  }
}
</style>
